import { PortalModule } from '@angular/cdk/portal';
import { NgIf } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Observable, Subject, asyncScheduler, from, map, of, race, filter as rxjsfilter, switchMap, take, takeUntil, toArray } from 'rxjs';
import { HubStatus } from '@app/constants';
import { DialogOptions, DialogService, FeedbackService, FlyoutService, LoadingService, SocketService } from '@app/core/services';
import { AccountType, ErrorMessages } from '@app/enums';
import { AccountPagingModel } from '@app/models/fund-management';
import { BaseFlyoutComponent, FileUploadComponent } from '@app/shared/components';
import { UploadedFileModel } from '@app/shared/components/file-upload/models/uploaded-file.model';
import { AccountService } from '@app/shared/services';
import { TransactionHistoryService } from '@app/shared/services/transaction-history.service';
import { LinqSocketsService } from '@app/shared/sockets/services';
import { HubData } from '@app/shared/sockets/shared/interfaces';
import { isNil } from '@app/utils';
import { CsvBudgetModel, CsvReader } from '@app/utils/csvReader';
import { SetupBudgetImportConfirmModalComponent } from '../../..';
import { SetupBudgetAccountModel } from './interfaces/setup-budget-account.model';
import { SetupBudgetImportStatusModel } from './interfaces/setup-budget-import-status.model';

@Component({
	selector: 'fm-setup-budget-import',
	templateUrl: './setup-budget-import.component.html',
	styleUrls: ['./setup-budget-import.component.less'],
	standalone: true,
	imports: [PortalModule, MatIconModule, FormsModule, FileUploadComponent, NgIf, MatButtonModule],
})
export class SetupBudgetImportComponent extends BaseFlyoutComponent implements OnDestroy, OnChanges {
	@ViewChild('importForm') importForm: NgForm;
	@ViewChild('fileUploadComponent') fileUploadComponent: FileUploadComponent;

	@Input() accountPaging: AccountPagingModel;
	@Input() fiscalYear: number;

	@Output() importResult = new EventEmitter<boolean>();

	uploadedFile: UploadedFileModel = {} as UploadedFileModel;

	fileBinary: string = null;
	fileName: string = null;
	maxSize = 50000000;
	validExtensions: string[] = ['.csv'];
	header: string;

	csvFileModel: CsvBudgetModel[] = [];

	private readonly destroy$ = new Subject();

	constructor(
		protected readonly dialog: MatDialog,
		protected readonly dialogService: DialogService,
		protected readonly loadingService: LoadingService,
		protected readonly feedbackService: FeedbackService,
		protected readonly flyoutService: FlyoutService,
		protected readonly socketService: SocketService,
		protected readonly linqSocketsService: LinqSocketsService,
		private readonly accountService: AccountService,
		private readonly transactionHistoryService: TransactionHistoryService
	) {
		super(flyoutService, dialogService);
	}
	ngOnChanges(changes: SimpleChanges): void {
		if (changes.fiscalYear.previousValue !== changes.fiscalYear.currentValue) {
			this.header = `Upload CSV File ( Fiscal Year: ${this.fiscalYear} )`;
		}
	}

	@HostListener('window:beforeunload')
	canDeactivate = (): boolean => true;

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	fileUploaded(uploadedFile: UploadedFileModel): void {
		this.uploadedFile.fileBase64Encoded = uploadedFile?.fileBase64Encoded;
		this.uploadedFile.fileName = uploadedFile?.fileName;
		if (uploadedFile?.filePlainText) {
			this.csvFileModel = CsvReader.readBudget(uploadedFile.filePlainText);

			this.accountService
				.getAllAccountBudgets(this.accountPaging, this.fiscalYear, true)
				.pipe(
					switchMap((processId: any) => {
						this.loadingService.show();
						return this.monitorSocketProcess(processId);
					})
				)
				.subscribe((result: any) => {
					const { Status, Data, Id, Message } = result;

					if (Status === HubStatus.Success) {
						this.displayResults(Data);
					} else {
						const errorMessage = Message?.Description || ErrorMessages.ErrorContactAdministrator;
						this.feedbackService.error(errorMessage.trim());
					}
					this.loadingService.hide();
					if (!isNil(Id)) {
						asyncScheduler.schedule(() => this.linqSocketsService.removeProcess(Id), 500);
					}
				});
		}
	}

	public closeFlyout(): void {
		this.close()
			.pipe(take(1))
			.subscribe((closed) => {
				if (closed) {
					this.uploadedFile = {} as UploadedFileModel;
				}
			});
	}

	private monitorSocketProcess(processId: string): Observable<HubData> {
		return race(
			this.socketService.error$.pipe(
				takeUntil(this.destroy$),
				rxjsfilter((notification: HubData) => notification.Id === processId),
				take(1)
			),
			this.socketService.success$.pipe(
				takeUntil(this.destroy$),
				rxjsfilter((notification: HubData) => notification.Id === processId),
				take(1)
			)
			// eslint-disable-next-line no-mixed-spaces-and-tabs
		);
	}

	private getImportedResults(budgetData: SetupBudgetAccountModel[]): Observable<SetupBudgetImportStatusModel[]> {
		return from(this.csvFileModel).pipe(
			map((csvModel) => {
				const account = budgetData.find((acct: SetupBudgetAccountModel) => acct.AccountNumber === csvModel.AccountNumber);
				if (account) {
					const amountValidationMessage = this.validateFileBudgetTotal(csvModel.Amount, account.AccountTypeId);
					if (!isNil(amountValidationMessage)) {
						return {
							Id: null,
							AccountNumber: csvModel.AccountNumber,
							BudgetTotal: csvModel.Amount,
							Message: amountValidationMessage,
							Invalid: true,
						} as SetupBudgetImportStatusModel;
					}

					return {
						Id: account.Id,
						AccountNumber: csvModel.AccountNumber,
						BudgetTotal: csvModel.Amount,
						Message: 'Imported successfully.',
						Invalid: false,
					} as SetupBudgetImportStatusModel;
				} else {
					return {
						Id: null,
						AccountNumber: csvModel.AccountNumber,
						BudgetTotal: csvModel.Amount,
						Message: 'Account is invalid.',
						Invalid: true,
					} as SetupBudgetImportStatusModel;
				}
			}),
			toArray()
		);
	}

	private validateFileBudgetTotal(budgetTotal: string, accountTypeId: number): string {
		const amount = Number(budgetTotal);
		if (isNil(budgetTotal) || isNaN(amount) || budgetTotal.trim().length === 0) {
			return 'Budget Total is invalid.';
		}

		const regex = /^-?\d+(\.\d{1,2})?$/; // positive or negative number with up to 2 decimal places
		if (!regex.test(budgetTotal)) {
			return 'Budget Total is invalid number.';
		}

		if (accountTypeId === AccountType.Revenue) {
			return amount <= 0 ? null : 'Revenue Budget Total must be negative or zero.';
		}

		return amount >= 0 ? null : 'Expense Budget Total must be positive.';
	}

	private displayResults(budgetData: any[]): void {
		this.getImportedResults(budgetData)
			.pipe(
				switchMap((importResults: SetupBudgetImportStatusModel[]) => {
					const successCount = importResults.filter((item) => !item.Invalid).length || 0;
					const errorCount = importResults.filter((item) => item.Invalid).length || 0;
					const messageResult =
						`${successCount} file account${successCount === 1 ? '' : 's'} imported successfully.<br>` +
						`${errorCount} file account${errorCount === 1 ? '' : 's'} failed due to error.`;

					return this.dialog
						.open(SetupBudgetImportConfirmModalComponent, {
							disableClose: true,
							minWidth: '85vw',
							data: {
								title: 'Import Values Results',
								subtitle: messageResult,
								okText: 'Save Budget with imported values',
								okDisabled: successCount === 0,
								showCancel: true,
								removeContentPaddingTop: true,
								tableData: importResults,
							},
						})
						.afterClosed()
						.pipe(
							take(1),
							takeUntil(this.destroy$),
							switchMap((confirmOne) =>
								confirmOne
									? this.dialogService.open({
											disableClose: true,
											title: 'Save Imported Values Confirmation',
											message: `The following action will update Budget Accounts with the new imported values.
											Are you sure you want to continue?`,
											okText: 'Yes',
											showCancel: true,
											// eslint-disable-next-line no-mixed-spaces-and-tabs
									  } as DialogOptions)
									: of(null)
							),
							switchMap((confirmTwo) => {
								if (confirmTwo) {
									const transactions = importResults
										.filter((f) => !f.Invalid)
										.map((item) => ({ Id: item.Id, Amount: item.BudgetTotal }));
									return this.transactionHistoryService.saveBudgets(this.fiscalYear, transactions, true).pipe(
										switchMap((processId: any) => {
											this.loadingService.show();
											return this.monitorSocketProcess(processId);
										})
									);
								} else {
									return of(null);
								}
							})
						);
				})
			)
			.subscribe((result: any) => {
				if (result) {
					this.loadingService.hide();
					const { Status, Id, Message } = result;

					if (Status !== HubStatus.Success) {
						const errorMessage = Message?.Description || ErrorMessages.ErrorContactAdministrator;
						this.feedbackService.error(errorMessage.trim());
					}

					if (!isNil(Id)) {
						asyncScheduler.schedule(() => this.linqSocketsService.removeProcess(Id), 500);
						this.uploadedFile = {} as UploadedFileModel;
						this.toggleFlyout();
						this.importResult.emit(true);
					}
				}
			});
	}
}
